import React, {forwardRef, ReactElement} from 'react';
import {MuiThemeProvider, useTheme} from '@material-ui/core';
import MaterialTable, {
  Icons,
  Localization,
  MaterialTableProps,
  MTableToolbar,
  Options
} from 'material-table';
import {useTranslation} from 'react-i18next';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import SkipNextIcon from '@material-ui/icons/SkipNext';
import SkipPreviousIcon from '@material-ui/icons/SkipPrevious';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import ReplayIcon from '@material-ui/icons/Replay';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import CheckIcon from '@material-ui/icons/Check';
import AddIcon from '@material-ui/icons/Add';

import {
  SearchOutlined as SearchIcon,
  ClearOutlined as ClearIcon
} from '@material-ui/icons';
import {
  DEFAULT_WHITE_COLOR,
  TABLE_ODD_ROW_COLOR
} from '../../../style/variable';

interface GridTableProps extends MaterialTableProps<any> {
  dummyProp?: string;
}
/*
 use interface Column from
 */
export const DEFAULT_GRID_TABLE__OPTIONS: Options<any> = {
  searchFieldStyle: {
    paddingBottom: 0
  },
  exportAllData: true,
  showEmptyDataSourceMessage: true,
  search: false,
  showTitle: false,
  toolbar: false,
  paging: false,
  sorting: false,
  paginationType: 'normal',
  thirdSortClick: true,
  rowStyle: (rowData: any, index) => {
    if (index % 2) {
      return {backgroundColor: DEFAULT_WHITE_COLOR, fontSize: '0.875rem'};
    } else {
      return {backgroundColor: TABLE_ODD_ROW_COLOR, fontSize: '0.875rem'};
    }
  }
};

const GridTable = (props: GridTableProps): ReactElement => {
  const theme = useTheme();
  const {t} = useTranslation();
  const {options, localization, icons, components, ...rest} = props;

  /*
  get the emptyDataSourceMessage from options here,
  if no data then show this message
  */
  const {emptyDataSourceMessage}: any = options ?? {emptyDataSourceMessage: ''};

  /*
	default options needs to be set here
	 */
  const defaultOptions: Options<any> = {
    draggable: false,
    sorting: true,
    thirdSortClick: false,
    ...options
  };
  /*
	for any global/default localization, please enter parameters here,
	it is configurable via passing objects too
	 */
  const defaultLocalization: Localization = {
    body: {
      emptyDataSourceMessage: emptyDataSourceMessage
        ? emptyDataSourceMessage
        : t('COMMON.NO_DATA_GRID')
    },
    ...localization
  };
  const defaultIcons: Icons = {
    SortArrow: forwardRef((props1, ref1) => (
      <ArrowDropUpIcon {...props1} ref={ref1} />
    )),
    Search: forwardRef((props1, ref1) => <SearchIcon {...props1} ref={ref1} />),
    ResetSearch: forwardRef((props1, ref1) => (
      <ClearIcon {...props1} ref={ref1} />
    )),
    LastPage: forwardRef((props1, ref1) => (
      <SkipNextIcon {...props1} ref={ref1} />
    )),
    FirstPage: forwardRef((props1, ref1) => (
      <SkipPreviousIcon {...props1} ref={ref1} />
    )),
    NextPage: forwardRef((props1, ref1) => (
      <NavigateNextIcon {...props1} ref={ref1} />
    )),
    PreviousPage: forwardRef((props1, ref1) => (
      <NavigateBeforeIcon {...props1} ref={ref1} />
    )),
    Retry: forwardRef((props1, ref1) => <ReplayIcon {...props1} ref={ref1} />),
    Edit: forwardRef((props1, ref1) => <EditIcon {...props1} ref={ref1} />),
    Delete: forwardRef((props1, ref1) => <DeleteIcon {...props1} ref={ref1} />),
    Clear: forwardRef((props1, ref1) => <ClearIcon {...props1} ref={ref1} />),
    Check: forwardRef((props1, ref1) => <CheckIcon {...props1} ref={ref1} />),
    Add: forwardRef((props1, ref1) => <AddIcon {...props1} ref={ref1} />),
    ...icons
  };
  return (
    <MuiThemeProvider theme={theme}>
      <MaterialTable
        options={defaultOptions}
        localization={defaultLocalization}
        icons={defaultIcons}
        components={{
          Toolbar: propsToolbar => {
            return <MTableToolbar {...propsToolbar} />;
          },
          ...components
        }}
        {...rest}
      />
    </MuiThemeProvider>
  );
};

export default GridTable;
